import React from 'react';
import { Queue } from '../types';
import { Layers, AlertCircle, User, ArrowDownToLine } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface SidebarProps {
  queues: Queue[];
  activeQueueId: string;
  onSelectQueue: (id: string) => void;
  recentlyUpdatedQueueId?: string | null;
  updateNonce?: number;
}
const QUEUE_DESCRIPTIONS: Record<string, string> = {
  Credit_Pulled_10_Days: 'Loans stuck after credit pull for 10+ days',
  App_Out_10_Days: 'Applications sent but not returned for 10+ days',
  Package_Back_10_Days: 'Packages received but not processed for 10+ days',
  Withdrawn_10_Days: 'Withdrawn loans needing a second review',
  X24_Hour_Re_Register_List: 'Loans withdrawn and not re-registered within 24h',
  Credit_Alert_List_View: 'Loans with credit alerts needing attention',
  Credit_Retention_List: 'Loans requiring client retention outreach',
  My_Claimed_Loans: 'Loans you have claimed and are working on'
};
export function Sidebar({
  queues,
  activeQueueId,
  onSelectQueue,
  recentlyUpdatedQueueId,
  updateNonce = 0
}: SidebarProps) {
  const standardQueues = queues.filter(q => !q.isPinned);
  const pinnedQueues = queues.filter(q => q.isPinned);
  const renderQueue = (queue: Queue) => {
    const isActive = activeQueueId === queue.id;
    const isUpdated = recentlyUpdatedQueueId === queue.id;
    const description = QUEUE_DESCRIPTIONS[queue.apiName] || 'Queue';
    return <motion.button key={isUpdated ? `${queue.id}-${updateNonce}` : queue.id} onClick={() => onSelectQueue(queue.id)} animate={isUpdated ? {
      backgroundColor: ['rgba(219,234,254,1)', 'rgba(239,246,255,1)'],
      boxShadow: ['0 0 0 3px rgba(59,130,246,0.45)', '0 0 0 0px rgba(59,130,246,0)']
    } : {}} transition={{
      duration: 1.4,
      ease: 'easeOut'
    }} className={`relative w-full text-left px-3 py-3 rounded-xl mb-1.5 flex items-start gap-3 transition-colors group ${isActive ? 'bg-blue-50 border border-blue-100 shadow-sm' : 'hover:bg-slate-100 border border-transparent'}`} data-id="element-209">
        <div className={`mt-0.5 ${isActive ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-600'}`} data-id="element-210">
          {queue.isPinned ? <User size={18} data-id="element-211" /> : queue.isOpportunityOnly ? <AlertCircle size={18} data-id="element-212" /> : <Layers size={18} data-id="element-213" />}
        </div>
        <div className="flex-1 min-w-0" data-id="element-214">
          <div className="flex items-center justify-between gap-2 mb-0.5" data-id="element-215">
            <span className={`text-sm font-semibold truncate ${isActive ? 'text-blue-900' : 'text-slate-700'}`} data-id="element-216">
              {queue.name}
            </span>
            <motion.span animate={isUpdated ? {
            scale: [1, 1.35, 1]
          } : {
            scale: 1
          }} transition={{
            duration: 0.6,
            ease: 'easeOut'
          }} className={`text-xs font-bold px-2 py-0.5 rounded-full ${isUpdated ? 'bg-blue-600 text-white' : isActive ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`} data-id="element-217">
              {queue.count}
            </motion.span>
          </div>
          <p className={`text-xs leading-snug pr-2 ${isActive ? 'text-blue-700/80' : 'text-slate-500'}`} data-id="element-218">
            {description}
          </p>
        </div>

        {/* Transient "added" badge */}
        <AnimatePresence data-id="element-219">
          {isUpdated && <motion.span initial={{
          opacity: 0,
          y: 6,
          scale: 0.9
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -6,
          scale: 0.9
        }} transition={{
          duration: 0.3
        }} className="absolute -top-2 right-3 flex items-center gap-1 bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md" data-id="element-220">
              <ArrowDownToLine size={11} data-id="element-221" />
              Loan added
            </motion.span>}
        </AnimatePresence>
      </motion.button>;
  };
  return <aside className="w-[300px] border-r border-slate-200 bg-slate-50/50 flex flex-col h-full shrink-0" data-id="element-222">
      <div className="p-4 flex-1 overflow-y-auto" data-id="element-223">
        <div className="mb-6" data-id="element-224">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 px-1" data-id="element-225">
            Action Queues
          </h3>
          {standardQueues.map(renderQueue)}
        </div>
      </div>

      <div className="p-4 border-t border-slate-200 bg-white" data-id="element-226">
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 px-1" data-id="element-227">
          My Workspace
        </h3>
        {pinnedQueues.map(renderQueue)}
      </div>
    </aside>;
}